import { useAssetStore } from './asset'
import { useAuthStore } from './auth'
import { useSessionStore } from './session'
import type {
	AFPAppointmentResult,
	ApiPriceBook,
	ApiV4FuneralHome,
	AppointmentFindResult,
	CounselorAppointment,
	CounselorAppointmentsResult,
	FuneralHomeGroup,
	includedReportFh,
	InsuranceCarrierContract,
	PapiResult,
	V3FuneralHome,
	V4FuneralHome,
} from '@/apiTypes'
import { PriceBook } from '@/classes/PriceBook'
import {
	type InsuranceProduct,
	insuranceProducts,
} from '@/data/insuranceProducts'
import { OapProvider, oapProviders } from '@/data/oapProviders'
import { axiosInstance } from '@/services/axiosInstance'
import { Feature, useFeatureService } from '@/services/featureService'
import { mapV4FieldsToCounselorAppointmentResult } from '@/services/v4MappingService'
import type { Downloadable } from '@/types'
import { Bump } from '@precoa/calculator-sdk'
import { captureException } from '@sentry/vue'
import localforageLib from 'localforage'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { ref, toRaw } from 'vue'

export type StoredAppointments = Downloadable<Array<CounselorAppointment>>
export type InsuranceProductDetails = InsuranceProduct & {
	bump: Bump
}

const localforage = localforageLib.createInstance({
	name: 'crm',
})

/** Luxon for beginning of 90 days prior to today */
function beginningOfThreeMonthsBack() {
	return DateTime.now()
		.minus({ days: 90 })
		.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
}

/** Luxon for beginning of 30 days prior to today */
function beginningOfOneMonthBack() {
	return DateTime.now()
		.minus({ days: 30 })
		.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
}

/** Luxon for the beginning of the day today */
function beginningOfToday() {
	return DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
}

export const useCrmStore = defineStore('crm', () => {
	const auth = useAuthStore()
	const appointments = ref<StoredAppointments>()
	const funeralHomes = ref<V4FuneralHome[]>()
	const sessionStore = useSessionStore()
	const priceBooks = ref<PriceBook[]>([])
	const assignedV3FuneralHomes = ref<V3FuneralHome[]>()
	const isUsingSfIntegration = ref(
		useFeatureService().isEnabled(Feature.SalesforceIntegration)
	)

	/** List of URLs used in price books. Exposed so they aren't deleted */
	const priceBookUrls = ref<Array<string>>([])

	/** Try to download a new batch of appts from the API */
	async function getAppointmentsFromServer() {
		const startBeforeDate = beginningOfToday().plus({ month: 3 }).toISO()
		const startAfterDate = beginningOfThreeMonthsBack().toISO()
		const v3Scopes = [
			'dispatched',
			'startAfter=' + startAfterDate,
			'startBefore=' + startBeforeDate,
		]
		const v4Scopes = [
			'afp::' + auth.user?.salesContact?.id,
			'startAfter::' + startAfterDate,
			'startBefore::' + startBeforeDate,
		]

		let result = isUsingSfIntegration.value
			? await getV4AppointmentsFromServer(v4Scopes)
			: (await getV3AppointmentsFromServer(v3Scopes)).data.data

		// Merge appointments with local appts
		const merged = toRaw(appointments.value?.data || [])

		for (const appt of result) {
			const index = merged.findIndex((a) => a.id === appt.id)
			if (index > -1) {
				merged[index] = appt
			} else {
				merged.push(appt)
			}
		}

		// Keep only appts that are unresulted, in the future, or are awaiting sync
		const filtered = merged.filter((appt) => {
			const session = sessionStore.getSessionForAppointment(appt.id)
			const status = session
				? sessionStore.requestQueue.getRequestStatus(session.id)
				: null

			return (
				// Appt is awaiting sync
				(status &&
					status.uploadStatus in ['failed', 'waitingForNetwork', 'sending']) ||
				// Appt is unresulted
				appt.resultId === null ||
				// Appt is within the lookup period
				appt.startAt > beginningOfThreeMonthsBack().toISO()
			)
		})

		// Sort
		const sortedAppts = filtered.sort((a, b) =>
			a.startAt.localeCompare(b.startAt)
		)

		// change (no string passed) to an empty string
		sortedAppts.forEach((item) => {
			if (item.lead?.data) {
				if (item.lead.data.address === '(no string passed)') {
					item.lead.data.address = ''
				}
				if (item.lead.data.city === '(no string passed)') {
					item.lead.data.city = ''
				}
			}
		})

		const apptsObj = {
			data: sortedAppts,
			downloadedAt: DateTime.now().toISO(),
		}

		appointments.value = apptsObj

		const promise = localforage
			.setItem('appointments', apptsObj)
			.catch(console.error)

		return promise
	}

	async function getV3AppointmentsFromServer(scopes: Array<string>) {
		return await axiosInstance.get<CounselorAppointmentsResult>(
			'/v3/counselors/' + auth.user?.salesContact?.id + '/appointments',
			{
				params: {
					scope: scopes.join('|'),
					include: 'funeralHome,lead.funeralHome,campaignManager,event',
				},
			}
		)
	}

	async function getV4AppointmentsFromServer(scopes: Array<string>) {
		return await axiosInstance
			.get<AFPAppointmentResult>('v4/appointments', {
				params: {
					scope: scopes.join('|'),
					include:
						'funeralHome,cepEvent,opportunity.opportunityContactRoles:scope(individual).contact',
					// Theoretical max number of appointments in 4 months
					itemsPerPage: 1000,
				},
			})
			.then((result) =>
				result.data.data.map((appt) =>
					mapV4FieldsToCounselorAppointmentResult(appt)
				)
			)
	}

	/** Try to fetch cached appointments from local storage */
	async function getAppointmentsFromStorage() {
		const result = await localforage.getItem<StoredAppointments>('appointments')

		if (result && !appointments.value) {
			appointments.value = result
		}
	}

	/*** Funeral Homes ***/
	function setAssignedV3FuneralHomes(
		funeralHomes: V3FuneralHome[] | undefined
	) {
		assignedV3FuneralHomes.value = funeralHomes
	}

	/** Given an array of v3 funeral homes, determine which FH groups and individual FHs to download */
	function getV3GroupIds(funeralHomes: V3FuneralHome[]): number[] {
		const crmGroupIdSet = new Set<number>()

		funeralHomes.forEach((fh) => {
			if (fh.groupId) {
				crmGroupIdSet.add(fh.groupId)
			}
		})

		return Array.from(crmGroupIdSet)
	}

	/** Given an array of v4 funeral homes, determine which FH groups and individual FHs to download */
	function vetV4GroupIds(funeralHomes: V4FuneralHome[]): number[] {
		const crmGroupIdSet = new Set<number>()

		funeralHomes.forEach((fh) => {
			if (fh.crmGroupId) {
				crmGroupIdSet.add(fh.crmGroupId)
			}
		})

		return Array.from(crmGroupIdSet)
	}

	/** Try to fetch cached funeral homes from local storage */
	async function getFuneralHomesFromStorage() {
		const result = await localforage.getItem<V4FuneralHome[]>('funeralHomes')

		if (result && !funeralHomes.value) {
			funeralHomes.value = result
		}
	}

	/** Collect details about the insurance product in an easy-to-use object */
	function getInsuranceDetails(
		contract: InsuranceCarrierContract
	): InsuranceProductDetails | undefined {
		const name = contract?.firstLineItem?.data?.product?.data?.name
		if (name === undefined) {
			console.warn('Could not find insurance product')
			return undefined
		}

		const product = insuranceProducts.find(
			(insuranceProduct) => insuranceProduct.name === name
		)

		if (!product) return undefined

		const bump: Bump =
			contract.productBump > 0.004
				? Bump.Fifty
				: contract.productBump > 0.002
					? Bump.TwentyFive
					: Bump.Zero

		return {
			...product,
			bump,
		}
	}

	/** Get the preferred NGL contract for a funeral home */
	function selectPreferredContract(
		contracts: InsuranceCarrierContract[]
	): InsuranceCarrierContract | undefined {
		if (!contracts.length) return undefined

		const nglContract = contracts.find(
			(c) => c.carrier === 'NGL' && c.status === 'Active'
		)

		const fdlContract = contracts.find(
			(c) => c.carrier === 'FDL' && c.status === 'Active'
		)

		// If a funeral home only has FDL contracts, do not generate a Sentry Error
		if (!nglContract && fdlContract) {
			console.debug('funeral home only has active FDL contracts')
			return undefined
		}

		if (!nglContract) {
			const message =
				'[transformApiFuneralHome] Expected an active NGL contract'
			const details = contracts.map((c) => `${c.carrier} (${c.status})`)

			console.error(message, details)

			captureException(new Error(message), {
				extra: {
					contracts: details,
				},
			})

			return undefined
		}

		return nglContract
	}

	/** Transform a funeral home from the API to a V4 Funeral Home */
	function transformApiFuneralHome(
		fh: ApiV4FuneralHome & includedReportFh,
		crmGroupId?: number
	): V4FuneralHome {
		// Get the contracts, local FH first
		const contracts = (fh.carrierContracts?.data ?? []).concat(
			fh.reportFh?.data.carrierContracts?.data ?? []
		)

		// Use the preferred NGL contract if available
		// Report to Sentry if NGL contract cannot be found, and it's not a FDL funeral home
		const selectedContract = selectPreferredContract(contracts)

		// Get a nicely formatted version of the insurance product
		const insuranceProduct = selectedContract
			? getInsuranceDetails(selectedContract)
			: undefined

		return {
			...fh,

			// include crmGroupId
			crmGroupId,

			// Use the reportFh's OAP if this FH doesn't have one
			oap: fh.oap ?? fh.reportFh?.data.oap,

			// Use the reportFh's insurance product if this FH doesn't have one
			carrierContracts: undefined,

			insuranceProduct,

			reportFh: undefined,
		} as V4FuneralHome
	}

	/** Given the list of FHs the agent works with, download all FHs in those FH groups */
	async function getFuneralHomesFromServer() {
		if (isUsingSfIntegration.value) {
			return []
		}

		let groupIdsToDownload: number[]

		// If the user just logged in, we will have included their v3 FHs with their sales contact
		if (assignedV3FuneralHomes.value !== undefined) {
			groupIdsToDownload = getV3GroupIds(assignedV3FuneralHomes.value)

			// If we just refreshed the page, we will have the v4 FHs already downloaded.
			// We still want to refresh them in case there are changes.
		} else if (funeralHomes.value !== undefined) {
			groupIdsToDownload = vetV4GroupIds(funeralHomes.value)

			// The FHs previously failed to download
		} else {
			throw new Error('Cannot determine which funeral homes to download')
		}

		// Items to include on each funeral home
		const includes = [
			'studioPriceBooks',

			// Get FH OAP details
			'oap.firstLineItem.product',
			'reportFh.oap.firstLineItem.product',

			// Get FH insurance product + report-level product
			'carrierContracts:scope(carrierType=Insurance|active).firstLineItem.product',
			'reportFh.carrierContracts:scope(carrierType=Insurance|active).firstLineItem.product',
		]

		const { getAssetBlobUrl, getThePlanningCenterImageUrl } = useAssetStore()

		// Get all the FHs in each group
		try {
			const fhGroups = await Promise.all(
				groupIdsToDownload.map(async (groupId) => {
					const response = await axiosInstance.get<
						PapiResult<FuneralHomeGroup[]>
					>(
						'/v4/fhgroups?include=' +
							includes.map((i) => 'funeralHomes.' + i).join(',') +
							'&scope=crmid::' +
							groupId
					)

					return response.data.data.map((res) =>
						res.funeralHomes!.data.map((fh) =>
							transformApiFuneralHome(fh, groupId)
						)
					)
				})
			)

			// Store as a flat array
			const fhs: V4FuneralHome[] = fhGroups.flat(2)
			await localforage.setItem('funeralHomes', fhs)
			Promise.all(
				fhs
					.filter((fh) => fh.logoUrl)
					.map((fh) => {
						const proxyUrl = getThePlanningCenterImageUrl(fh.logoUrl!)
						return getAssetBlobUrl(proxyUrl)
					})
			)
			funeralHomes.value = fhs
		} catch (error) {
			console.error('Failed to download funeral homes from server')
			throw error
		}
	}

	/** Download a funeral home group from PAPI */
	async function getFuneralHomeGroupById(groupId: string) {
		try {
			const response = await axiosInstance.get<PapiResult<FuneralHomeGroup>>(
				'/v4/fhgroups/' + groupId + '?include=funeralHomes'
			)

			return response.data.data
		} catch (error) {
			console.error('Could not get FHs by groupId', groupId)
			throw error
		}
	}

	/** All assets needed for this sales contact's OAP providers */
	function getOapProviderUrls(): string[] {
		const allProviders = funeralHomes.value
			?.map((fh) => fh.oap?.data?.carrier)
			.filter((provider) => provider) as OapProvider[]

		const uniqueProviderNames = Array.from(new Set(allProviders))

		return uniqueProviderNames.map(
			(providerName) => oapProviders[providerName].imageUrl
		)
	}

	/** Precache assets for the OAP providers */
	async function precacheOapProviders() {
		const { getAsset } = useAssetStore()

		return Promise.all(getOapProviderUrls().map((url) => getAsset(url)))
	}

	/** Delete everything from local storage */
	async function purge() {
		// Cancel pending Axios requests
		appointments.value = undefined
		funeralHomes.value = []
		priceBooks.value = []
		assignedV3FuneralHomes.value = undefined
		await localforage.clear()
	}

	/** Get an appointment by id */
	function findAppointment(id: string): CounselorAppointment | undefined {
		return appointments.value?.data.find((a) => a.id === id)
	}

	/** Fetch an appointment from the server by id */
	async function fetchAppointment(id: string) {
		const result = await axiosInstance.get<AppointmentFindResult>(
			'/v3/appointments/' +
				id +
				'?include=funeralHome,lead.funeralHome,campaignManager,event'
		)

		return result.data
	}

	/** Get a funeral home by id */
	function findFuneralHome(id: string) {
		return funeralHomes.value?.find((fh) => fh.id === id)
	}

	function findFuneralHomesInGroup(groupId: string) {
		return funeralHomes.value?.filter((fh) => fh.groupId === groupId)
	}

	/** Get the list of unique funeral home group ids */
	function findFhGroupIds() {
		return Array.from(
			new Set(funeralHomes.value?.map((fh) => fh.groupId) || [])
		).filter((id) => id != null)
	}

	/***  Price Books ***/
	/** Get the price books for all our funeral homes from PAPI */
	async function getPriceBooksFromServer() {
		priceBookUrls.value = []

		const bookIds =
			funeralHomes.value
				?.map((fh) => fh.studioPriceBooks?.data.map((pb) => pb.id))
				.flat(2) ?? []
		const uniqueBookIds = Array.from(new Set(bookIds))
		const books = await Promise.all(
			uniqueBookIds.map(async (bookId) => {
				const response = await axiosInstance.get<PapiResult<ApiPriceBook>>(
					'/v4/studiopricebooks/' +
						bookId +
						'?include=lineItems.packageRelations,lineItems.pricing,lineItems.topicAssignments.topic'
				)
				return response.data.data
			})
		)

		localforage.setItem('priceBooks', books)

		priceBooks.value = books.map((b) => new PriceBook(b))
		priceBookUrls.value = getPriceBookUrls(priceBooks.value)

		return downloadUrls(priceBookUrls.value)
	}

	/** Get a list of unique image urls in the given price books */
	function getPriceBookUrls(priceBooks: PriceBook[]): string[] {
		// Get all the product image urls
		let urls = priceBooks
			.map((pb) => pb.getLineItems().map((li) => li.productImageUrl))
			.flat(2)
			.filter((url) => url) as string[]

		// Add the pdf url
		urls = urls.concat(
			priceBooks
				.map((pb) => pb.data.externalGplUrl)
				.filter((url) => !!url) as string[]
		)

		return Array.from(new Set(urls))
	}

	/** Download the assets for the given URLs */
	function downloadUrls(urls: string[]): Promise<any> {
		const assetStore = useAssetStore()

		return Promise.allSettled(urls.map((url) => assetStore.getAsset(url)))
	}

	/** Load the price books from IndexedDB */
	async function getPriceBooksFromStorage() {
		const books = await localforage.getItem<ApiPriceBook[]>('priceBooks')

		if (!books) {
			console.debug('No price books found in storage')
			return
		}

		if (priceBooks.value.length > 0) {
			console.debug('Disregarding stored price books')
			return
		}

		priceBooks.value = books.map((b) => new PriceBook(b))
	}

	/** Get a price book by id */
	function findPriceBook(id: string): PriceBook | undefined {
		return priceBooks.value.find((pb) => pb.data.id === id)
	}

	/** Get the right price book for the given funeral home */
	function findPriceBookForFuneralHome(v4FhId: string): PriceBook | undefined {
		const pbId = findFuneralHome(v4FhId)?.studioPriceBooks?.data.at(0)?.id

		if (!pbId) return undefined

		return findPriceBook(pbId)
	}

	return {
		appointments,
		funeralHomes,
		fetchAppointment,
		findAppointment,
		findFuneralHome,
		findFhGroupIds,
		findFuneralHomesInGroup,
		findPriceBook,
		findPriceBookForFuneralHome,
		getAppointmentsFromServer,
		getAppointmentsFromStorage,
		getFuneralHomesFromServer,
		getFuneralHomesFromStorage,
		getPriceBooksFromServer,
		getPriceBooksFromStorage,
		getFuneralHomeGroupById,
		getOapProviderUrls,
		precacheOapProviders,
		priceBooks,
		priceBookUrls,
		purge,
		setAssignedV3FuneralHomes,

		// For testing only
		getPriceBookUrls,
	}
})
